import { useMemo, useState } from 'react';
import { useAppState } from '@/ui/AppState';
import { navigate, useRoute } from '@/ui/router';
import { CategoryChip, EmptyState, FlagBanner, SessionChip } from '@/ui/components/bits';
import { ModelAnswer } from '@/ui/components/ModelAnswer';
import type { Item } from '@/domain/types';

const MAX_RESULTS = 60;

function haystack(item: Item): string {
  return [
    item.front,
    item.back,
    item.q,
    item.sample,
    item.answer,
    item.note,
    item.category,
    ...(item.opts ?? []),
    ...(item.steps ?? []),
    ...(item.parts ?? []).flatMap((p) => [p.prompt, p.sample]),
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();
}

function snippet(item: Item): string {
  return item.type === 'flashcard' ? (item.front ?? '') : (item.q ?? '');
}

export function SearchScreen() {
  const { items, sessions } = useAppState();
  // The vocabulary list links here with the term already in hand.
  const route = useRoute();
  const [query, setQuery] = useState(route.params.get('q') ?? '');
  const [openId, setOpenId] = useState<string | null>(null);

  const sessionLabel = useMemo(
    () => new Map(sessions.map((s) => [s.id, s.shortLabel])),
    [sessions],
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const terms = q.split(/\s+/);
    return items
      .filter((item) => {
        const hay = haystack(item);
        return terms.every((t) => hay.includes(t));
      })
      .slice(0, MAX_RESULTS);
  }, [items, query]);

  return (
    <div className="stack">
      <h1>Search</h1>
      <input
        className="input"
        type="search"
        value={query}
        placeholder="Search every card and question"
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
      />

      {query.trim().length >= 2 ? (
        <p className="faint" style={{ margin: 0 }}>
          {results.length === MAX_RESULTS ? `First ${MAX_RESULTS} matches` : `${results.length} match${results.length === 1 ? '' : 'es'}`}
        </p>
      ) : (
        <p className="faint" style={{ margin: 0 }}>
          Type at least two characters. Fronts, backs, questions, sample answers, options and
          categories are all searched.
        </p>
      )}

      {query.trim().length >= 2 && results.length === 0 ? (
        <EmptyState icon="⌕" title="Nothing found">
          Try a shorter search.
        </EmptyState>
      ) : null}

      <div>
        {results.map((item) => (
          <div key={item.id}>
            <button
              className="search-result"
              onClick={() => setOpenId(openId === item.id ? null : item.id)}
            >
              <div className="row" style={{ marginBottom: 4 }}>
                <CategoryChip category={item.category} />
                <SessionChip label={sessionLabel.get(item.sessionId) ?? item.sessionId} />
                <span className="chip chip--count">{item.type}</span>
              </div>
              <div className="search-result__snippet">{snippet(item)}</div>
            </button>

            {openId === item.id ? (
              <div className="card stack" style={{ marginBottom: 'var(--sp-3)' }}>
                {item.type === 'flashcard' ? (
                  <>
                    <span className="flashcard__label">Front</span>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{item.front}</div>
                    <span className="flashcard__label">Back</span>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{item.back}</div>
                  </>
                ) : (
                  <>
                    <span className="flashcard__label">Question</span>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{item.q}</div>
                    {item.opts ? (
                      <ol style={{ margin: 0, paddingLeft: '1.4em' }}>
                        {item.opts.map((opt, i) => (
                          <li
                            key={i}
                            style={{
                              color: i === item.answerIndex ? 'var(--correct)' : undefined,
                              fontWeight: i === item.answerIndex ? 600 : undefined,
                            }}
                          >
                            {opt}
                          </li>
                        ))}
                      </ol>
                    ) : null}
                    {item.type !== 'mc' || item.sample ? <ModelAnswer item={item} /> : null}
                    {item.note ? (
                      <div className="banner banner--info">
                        <strong>Why</strong>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{item.note}</div>
                      </div>
                    ) : null}
                  </>
                )}
                {item.flag ? <FlagBanner flag={item.flag} text={item.flagText} /> : null}
                <button
                  className="btn btn--sm"
                  onClick={() => navigate('/session', { id: item.sessionId })}
                >
                  Open {sessionLabel.get(item.sessionId) ?? 'session'}
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
